import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link, useParams } from 'react-router-dom';
import { categories, comics } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryComics = comics.filter((c) => c.categorySlug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500">未找到该分类</p>
            <Link to="/" className="mt-4 inline-block text-sm text-primary-500 hover:text-primary-600">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        <section className="pt-24 md:pt-28 pb-6 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs font-label text-foreground-500 mb-6">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <span>/</span>
              <span className="text-foreground-900 font-medium">{category.name}</span>
            </nav>

            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
              <div>
                <h1 className="font-heading text-3xl md:text-5xl font-black text-foreground-950 mb-2">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-600 max-w-lg">{category.description}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-foreground-500">共 {categoryComics.length} 部作品</span>
              </div>
            </div>

            <div className="flex items-center gap-1.5 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-label whitespace-nowrap transition-all duration-200 ${
                    cat.slug === slug
                      ? 'bg-foreground-950 text-background-50 font-semibold'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-sm`}></i>
                  </div>
                  {cat.name}
                  <span className={`text-xs ml-0.5 ${cat.slug === slug ? 'text-background-50/60' : 'text-foreground-400'}`}>
                    {cat.count}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {categoryComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group block"
                >
                  <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-background-100 mb-3">
                    <img
                      src={comic.coverImage}
                      alt={comic.title}
                      title={`${comic.title} - 韩漫漫画网${category.name}分类`}
                      className="w-full h-full object-cover transition-transform duration-400 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-background-50/90 backdrop-blur-sm text-xs font-label text-foreground-700">
                      {comic.status}
                    </div>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-1 line-clamp-1 group-hover:text-primary-500 transition-colors">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-500 line-clamp-1">{comic.latestChapter}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex items-center gap-0.5">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-star-fill text-xs text-accent-500"></i>
                      </div>
                      <span className="text-xs font-label text-foreground-600">{comic.rating}</span>
                    </div>
                    <span className="text-xs text-foreground-400">{comic.views}</span>
                  </div>
                </Link>
              ))}
            </div>

            {categoryComics.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
                  <i className="ri-book-open-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画，敬请期待</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'http://j3zb.com/' },
              { '@type': 'ListItem', position: 2, name: category.name, item: `http://j3zb.com/category/${slug}` },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name} - 韩漫漫画网`,
            description: category.description,
            url: `http://j3zb.com/category/${slug}`,
            mainEntity: {
              '@type': 'ItemList',
              itemListElement: categoryComics.map((comic, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                item: {
                  '@type': 'Book',
                  name: comic.title,
                  url: `http://j3zb.com/comic/${comic.id}`,
                  author: { '@type': 'Person', name: comic.author },
                },
              })),
            },
          }),
        }}
      />
    </>
  );
}